import classNames from 'classnames'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { LoaderOverlay } from 'core-components/library/loader'
import { ITabProps } from 'core-components/library/tabButtons'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import Logger from 'core/utils/Logger'
import { EExceptionRequestCrudKey } from 'onboarding/api/exceptionRequestApi'
import { showError, warning } from 'onboarding/classes/services/notification'
import {
  OnboardingCheckbox,
  OnboardingDropdown,
  OnboardingTextarea,
} from 'onboarding/components/ui/theme'
import { IExcCategory } from 'onboarding/components/widgets/public/admin/exceptionsReasons/models'
import { exceptionRequestService } from 'onboarding/services/exceptionRequestService'

import { CategorySelectionTooltip } from './categorySelectionInfo/CategorySelectionTooltip'
import {
  BUSINESS_UNITS,
  MAX_CATEGORIES_IN_BUSINESS_UNIT,
  SINGLE_CATEGORY_CRUD_PROPS,
} from './configs'
import { CycleEffectOptions } from './CycleEffectOptions'
import { BusinessUnitsWatcher } from './helpers/BusinessUnitsWatcher'
import { checkNeedThresholdConfirmation } from './helpers/checkThresholdConfirmation'
import { getCommonFieldsEmpty } from './helpers/getInitException'
import { mapToMultipleCategories } from './helpers/mapToMultipleCategories'
import { markCategoriesHierarchy, TMarkedCategory } from './helpers/markCategoriesHierarchy'
import { SelectCategories } from './hocs/SelectCategory'
import { IExceptionParams } from './models'
import { MultipleCategories, TCategory, TSubcategory } from './multipleCategories'
import { ReadOnlyExceptionInfo } from './ReadOnlyExceptionInfo'
import { ScrollTabs } from './scrollTabs/ScrollTabs'

import styles from './ExceptionWelcomeForm.module.scss'

export interface IExceptionWelcomeFormProps {
  onChange: (value: Partial<IExceptionParams>) => void
  formData: IExceptionParams
  serverFormData: IExceptionParams | null
  type: RequestTypes
  disabled?: boolean
  checkFn?: (value: string) => Promise<string>
  filePanel?: ReactNode
}

/**
 * todo: refactor: split to ExceptionFormCreate and ExceptionFormUpdate
 * domain -> group -> code (reason) -> categories
 * @param formData
 * @param onChange
 * @param isActionForm
 * @param checkFn
 * @param type
 * @param disabled
 * @param filePanel
 * @constructor
 */
export const ExceptionWelcomeForm = ({
  formData,
  onChange,
  serverFormData,
  checkFn,
  type,
  disabled,
  filePanel,
}: IExceptionWelcomeFormProps) => {
  const businessUnitsWatcher = useRef(
    new BusinessUnitsWatcher(BUSINESS_UNITS, MAX_CATEGORIES_IN_BUSINESS_UNIT)
  )
  const [isExceptionsFetching, setIsExceptionsFetching] = useState(false)
  const [isCategoriesFetching, setIsCategoriesFetching] = useState(false)

  const [domainsGroups, setDomainsGroups] = useState<Map<string, Set<string>> | null>(null)
  const [singleCategories, setSingleCategories] = useState<TMarkedCategory[]>([])
  const [multipleCategories, setMultipleCategories] = useState<TCategory[]>([])

  const activeDomain = useMemo(() => {
    let result = formData.ExcDomain
    const domains = domainsGroups ? Array.from(domainsGroups.keys()) : []
    if (!result && domains.length) {
      result = domains[0]
    }

    return result
  }, [formData.ExcDomain, domainsGroups])

  const activeGroup = useMemo(() => {
    let result = formData.Group
    if (!result) {
      const domainGroups = Array.from(domainsGroups?.get(activeDomain) ?? [])
      if (domainGroups.length) {
        result = domainGroups[0]
      }
    }

    return result
  }, [formData.Group, activeDomain, domainsGroups])

  const sourceExceptionReason = useMemo(() => {
    return activeDomain
      ? exceptionRequestService.getActiveExceptionsFilteredBy(type, {
          Domain: activeDomain,
          Group: activeGroup,
        })
      : exceptionRequestService.getEmptyExceptionsCrud()
  }, [activeDomain, activeGroup, type])

  const singleCategoryCode = useMemo(
    () => (formData.IsMultiSelect ? '' : (formData.ExceptionReason[0] ?? '')),
    [formData.ExceptionReason, formData.IsMultiSelect]
  )
  const singleCategorySource = useMemo(() => {
    return new Crud({
      hierarchyField: SINGLE_CATEGORY_CRUD_PROPS.hierarchyField,
      keyField: SINGLE_CATEGORY_CRUD_PROPS.keyField,
      fetcher: new Static(singleCategories),
      hierarchyRoot: null,
      defaultPaging: { page: 0, pageSize: 3000 },
    })
  }, [singleCategories])

  const domainTabs: ITabProps[] = useMemo(() => {
    const domains = domainsGroups ? Array.from(domainsGroups.keys()) : []
    return domains.map((domain) => ({
      id: domain,
      content: () => <></>,
      title: () => <span className={styles.tabContent}>{domain}</span>,
    }))
  }, [domainsGroups])

  const groupTabs: ITabProps[] = useMemo(() => {
    let groups: string[] = []
    if (activeDomain && domainsGroups) {
      const domainGroups = domainsGroups.get(activeDomain) ?? []
      groups = Array.from(domainGroups)
    }

    return groups.map((group) => ({
      id: group,
      content: () => <></>,
      title: () => <span className={styles.tabContent}>{group}</span>,
    }))
  }, [domainsGroups, activeDomain])

  const confirmPartnerThresholdData = useMemo(() => {
    return {
      exceptionCode: serverFormData?.ExcCode ?? formData.ExcCode,
      isConfirmed:
        serverFormData?.IsExceptionRequestPartnerThresholdConfirmed ??
        formData.IsExceptionRequestPartnerThresholdConfirmed,
      isSaved: Boolean(serverFormData?.ExcCode),
    }
  }, [
    formData.ExcCode,
    formData.IsExceptionRequestPartnerThresholdConfirmed,
    serverFormData?.ExcCode,
    serverFormData?.IsExceptionRequestPartnerThresholdConfirmed,
  ])

  const clearCategories = useCallback(() => {
    setSingleCategories([])
    setMultipleCategories([])
    businessUnitsWatcher.current.resetEntries()
  }, [])

  const changeDomain = useCallback(
    (value: string) => {
      onChange({
        ExcDomain: value,
        Group: '',
        ExcCode: '',
        ...getCommonFieldsEmpty(),
      })
      clearCategories()
    },
    [clearCategories, onChange]
  )
  const changeGroup = useCallback(
    (value: string) => {
      onChange({
        Group: value,
        ExcCode: '',
        ...getCommonFieldsEmpty(),
      })
      clearCategories()
    },
    [clearCategories, onChange]
  )
  const changeCode = useCallback(
    (value: string) => {
      onChange({
        ExcCode: value,
        ...getCommonFieldsEmpty(),
      })
      clearCategories()
    },
    [onChange, clearCategories]
  )

  const change = useCallback(
    (field: string) => (value: string | boolean) => onChange({ [field]: value }),
    [onChange]
  )

  const onSelectSingleCategory = useCallback(
    (code: string, item: TMarkedCategory) => {
      onChange({
        ExceptionReason: code ? [code] : [],
        ExceptionCategoryGUID: item.GUID ? [item.GUID] : [],
      })
    },
    [onChange]
  )

  const onSelectMultipleCategories = useCallback(
    ({ title, code, guid }: TSubcategory) => {
      const chosenCategory = formData.ExceptionCategoryGUID.find((i) => i === guid)
      if (chosenCategory) {
        businessUnitsWatcher.current.removeEntryIfExists(title)
        onChange({
          ExceptionReason: formData.ExceptionReason.filter((i) => i !== code),
          ExceptionCategoryGUID: formData.ExceptionCategoryGUID.filter((i) => i !== guid),
        })
      } else {
        const businessUnit = businessUnitsWatcher.current.getBusinessUnitInEntry(title)
        if (businessUnit) {
          if (businessUnitsWatcher.current.isBusinessUnitFull(businessUnit)) {
            warning(`Can not choose more categories in business unit '${businessUnit}'`)
          } else {
            businessUnitsWatcher.current.addEntry(businessUnit, title)
            onChange({
              ExceptionReason: [...formData.ExceptionReason, code],
              ExceptionCategoryGUID: [...formData.ExceptionCategoryGUID, guid],
            })
          }
        } else {
          onChange({
            ExceptionReason: [...formData.ExceptionReason, code],
            ExceptionCategoryGUID: [...formData.ExceptionCategoryGUID, guid],
          })
        }
      }
    },
    [formData.ExceptionReason, formData.ExceptionCategoryGUID, onChange]
  )

  const initSavedCategoriesInBusinessUnitsWatcher = useCallback(
    (items: IExcCategory[]) => {
      if (serverFormData?.ExceptionCategoryGUID && serverFormData?.IsMultiSelect && items.length) {
        businessUnitsWatcher.current.resetEntries()
        const titles = items
          .filter((category) => serverFormData.ExceptionCategoryGUID.includes(category.GUID))
          .map((category) => category.Title)
        businessUnitsWatcher.current.initEntries(titles)
      }
    },
    [serverFormData?.ExceptionCategoryGUID, serverFormData?.IsMultiSelect]
  )

  useEffect(() => {
    const fetchActiveExceptions = async () => {
      try {
        setIsExceptionsFetching(true)
        const { items, error } = await exceptionRequestService.getActiveExceptions(
          type,
          EExceptionRequestCrudKey.domains
        )
        if (error) {
          throw new Error(error)
        }

        const domainsGroups: Map<string, Set<string>> = new Map()
        items.forEach((item) => {
          if (item.Domain) {
            const groups = domainsGroups.get(item.Domain) ?? new Set()
            if (item.Group) {
              groups.add(item.Group)
            }
            domainsGroups.set(item.Domain, groups)
          }
        })
        setDomainsGroups(domainsGroups)
      } catch (e: unknown) {
        showError('Failed to fetch active exceptions')
        Logger.error(e)
      } finally {
        setIsExceptionsFetching(false)
      }
    }

    fetchActiveExceptions()
  }, [type])

  useEffect(() => {
    const setExceptionEffects = async () => {
      try {
        if (formData.ExcCode) {
          const { items, error } = await sourceExceptionReason.fetch()
          if (error) {
            throw new Error(error)
          }
          const exception = items.find((i) => i.Code === formData.ExcCode) ?? null
          if (exception) {
            onChange({
              IsCycleEffect: Boolean(exception.IsCycleEffect),
              IsMultiSelect: Boolean(exception.IsMultiSelect),
            })
          }
        }
      } catch (e: unknown) {
        showError('Failed to set exception effects')
        Logger.error(e)
      }
    }

    setExceptionEffects()
  }, [formData.ExcCode, onChange, sourceExceptionReason])

  useEffect(() => {
    const fetchExceptionCategories = async (): Promise<IExcCategory[]> => {
      if (!formData.ExcCode) {
        return []
      }

      try {
        setIsCategoriesFetching(true)
        clearCategories()
        const { items, error } = await exceptionRequestService
          .getCategoriesCrudFilteredByExceptionCode({
            type,
            exceptionCode: formData.ExcCode,
            isActive: true,
            crudKey: EExceptionRequestCrudKey.exceptionCategoriesList,
          })
          .fetch({ skipHierarchyFilter: true })
        if (error) {
          throw new Error(error)
        }

        if (formData.IsMultiSelect) {
          setMultipleCategories(mapToMultipleCategories(items))
        } else {
          setSingleCategories(
            markCategoriesHierarchy({
              categories: items,
              keyField: SINGLE_CATEGORY_CRUD_PROPS.keyField,
              relationField: SINGLE_CATEGORY_CRUD_PROPS.hierarchyField,
            })
          )
        }

        return items
      } catch (e: unknown) {
        showError('Failed to fetch exception categories')

        return []
      } finally {
        setIsCategoriesFetching(false)
      }
    }

    fetchExceptionCategories().then(initSavedCategoriesInBusinessUnitsWatcher)
  }, [
    clearCategories,
    initSavedCategoriesInBusinessUnitsWatcher,
    formData.ExcCode,
    formData.IsMultiSelect,
    type,
  ])

  return (
    <div className={styles.root}>
      <LoaderOverlay show={isExceptionsFetching} />
      {!serverFormData && Boolean(domainTabs.length) && (
        <ScrollTabs items={domainTabs} value={activeDomain} onChange={changeDomain} />
      )}
      {!serverFormData && Boolean(groupTabs.length) && (
        <ScrollTabs items={groupTabs} value={activeGroup} onChange={changeGroup} />
      )}

      <div
        className={classNames(styles.body, styles.marginBottom, {
          [styles.marginTop]: !serverFormData,
        })}
      >
        <div className={styles.body__dropdowns}>
          {serverFormData ? (
            <ReadOnlyExceptionInfo
              domain={serverFormData.ExcDomain}
              group={serverFormData.Group}
              code={serverFormData.ExcCode}
            />
          ) : (
            <OnboardingDropdown
              checkFn={checkFn}
              label={'Select Exception Reason'}
              source={sourceExceptionReason}
              onChange={changeCode}
              value={formData.ExcCode}
              renderFunction={(item) => {
                return <div>{item.Code}</div>
              }}
              caption={'Code'}
              disabled={disabled}
            />
          )}

          {Boolean(formData.ExcCode) && (
            <div className={styles.marginTop}>
              <SelectCategories
                isMultiple={formData.IsMultiSelect}
                hasCategories={
                  formData.IsMultiSelect
                    ? Boolean(multipleCategories.length)
                    : Boolean(singleCategories.length)
                }
                isLoading={isCategoriesFetching}
                singleCategoryComponent={
                  <OnboardingDropdown
                    checkFn={checkFn}
                    label={'Select Category'}
                    source={singleCategorySource}
                    itemClickCallback={(item) => {
                      return !item.HasChildren
                    }}
                    onChange={onSelectSingleCategory}
                    value={singleCategoryCode}
                    renderFunction={(item) => {
                      const category = item as TMarkedCategory

                      return (
                        <div className={styles.category}>
                          {!category.ParentCode && (
                            <CategorySelectionTooltip
                              guidelines={category.Guidelines || ''}
                              description={category.Description || ''}
                              justification={category.Justification || ''}
                              mode={'single'}
                            >
                              <span className={styles.categoryGuidelines}>View Guidelines</span>
                            </CategorySelectionTooltip>
                          )}
                          {category[SINGLE_CATEGORY_CRUD_PROPS.captionField]}
                        </div>
                      )
                    }}
                    caption={SINGLE_CATEGORY_CRUD_PROPS.captionField}
                    disabled={disabled}
                  />
                }
                multipleCategoryComponent={
                  <MultipleCategories
                    key={formData.ExcCode}
                    categories={multipleCategories}
                    businessUnits={BUSINESS_UNITS}
                    chosenGUIDs={formData.ExceptionCategoryGUID}
                    onChoose={onSelectMultipleCategories}
                    isDisabled={disabled}
                    isChosenExpanded={Boolean(serverFormData)}
                  />
                }
              />
            </div>
          )}
        </div>

        <CycleEffectOptions
          isVisible={formData.IsCycleEffect}
          value={formData.FlaggingEffect}
          onChange={change('FlaggingEffect')}
          isDisabled={disabled}
        />
      </div>

      {checkNeedThresholdConfirmation(type, confirmPartnerThresholdData.exceptionCode) ? (
        <div className={styles.marginBottom}>
          <OnboardingCheckbox
            label={'I confirm the partner has achieved at least 70% of the threshold'}
            checked={confirmPartnerThresholdData.isConfirmed}
            onChange={change('IsExceptionRequestPartnerThresholdConfirmed')}
            disabled={disabled || confirmPartnerThresholdData.isSaved}
            wrapper={confirmPartnerThresholdData.isConfirmed ? 'normal' : 'danger'}
            inline
          />
        </div>
      ) : null}

      <div className={styles.marginBottom}>
        <OnboardingTextarea
          label={'Business Justification'}
          value={formData.BusinessJustification}
          onChange={change('BusinessJustification')}
          rows={4}
          disabled={disabled}
        />
      </div>

      {filePanel}
    </div>
  )
}
